import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/cn'

import { PixelSpinner } from './Spinner'

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const variants: Record<Variant, string> = {
  primary: 'bg-brand text-on-brand hover:bg-brand-hover disabled:bg-brand/50',
  secondary: 'bg-ink text-surface hover:bg-ink/90 disabled:bg-ink/50',
  // 不画边框：阶梯角会把 1px 边框的四个角切断，看起来像散开的四根线
  outline: 'bg-surface-subtle text-ink hover:bg-line',
  ghost: 'text-ink hover:bg-surface-hover',
  danger: 'bg-danger text-surface hover:bg-danger/90',
}

const sizes: Record<Size, string> = {
  sm: 'h-8 px-4 text-sm',
  md: 'h-9 px-4 text-[15px]',
  lg: 'h-12 px-6 text-base',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  fullWidth?: boolean
  children?: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  className,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 px-corners font-bold transition-colors',
        // 按钮一律不折行，否则窄容器里「换一题」会被拆成两行
        'whitespace-nowrap',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {loading && <PixelSpinner className="size-4" />}
      {children}
    </button>
  )
}
